import { Dashes } from '@metatypes/typography'
import type {
	CellContext,
	ExpandedState,
	HeaderContext,
	RowSelectionState,
	VisibilityState,
} from '@tanstack/table-core'
import {
	createColumnHelper,
	getCoreRowModel,
	getExpandedRowModel,
} from '@tanstack/table-core'
import { useVueTable } from '@tanstack/vue-table'
import classNames from 'classnames'
import { computed, h, ref, type Ref, watch } from 'vue'
import { z } from 'zod'

import { Yoco, yocoIconSchema } from '@3yourmind/yoco'

import { KtButton } from '../../kotti-button/'
import { useI18nContext } from '../../kotti-i18n/hooks'
import { KtPopover } from '../../kotti-popover/'
import ToggleInner from '../../shared-components/toggle-inner/ToggleInner.vue'
import ToggleRadio from '../../shared-components/ToggleRadio.vue'

import { type TableContext, useProvideTableContext } from './context'
import type { GetRowBehavior, KottiTable } from './types'
import { type ReactStyleUpdater, useComputedRef } from './use-computed-ref'

export const EXPANSION_COLUMN_ID = 'internal-expand-column'
export const MULTI_SELECTION_COLUMN_ID = 'internal-single-select-column'
export const SINGLE_SELECTION_COLUMN_ID = 'internal-multi-select-column'
const ARRAY_START = 3

type SortingState<COLUMN_ID extends string = string> = {
	desc: boolean
	id: COLUMN_ID
}[]

type InternalColumnId<COLUMN_ID extends string = string> =
	| COLUMN_ID
	| typeof EXPANSION_COLUMN_ID
	| typeof MULTI_SELECTION_COLUMN_ID
	| typeof SINGLE_SELECTION_COLUMN_ID

export type KottiTableParameter<
	ROW extends KottiTable.AnyRow,
	COLUMN_ID extends string,
> = {
	columns: KottiTable.Column<ROW, COLUMN_ID>[]
	data: ROW[]
	expandMode?: 'multi' | 'single'
	getRowBehavior: GetRowBehavior<ROW>
	hasDragAndDrop?: boolean
	id: string
	isSelectable?: 'multi' | 'single' | boolean
}

export const paramsSchema = z
	.object({
		columns: z.array(
			z
				.object({
					display: z
						.object({
							align: z.enum(['center', 'left', 'right']),
							disableCellClick: z.boolean(),
							isNumeric: z.boolean(),
							render: z.function(),
							sortBehavior: z.enum(['asc-desc', 'desc-asc']),
						})
						.strict(),
					getData: z.function(),
					id: z.string(),
					isSortable: z.boolean().default(false),
					label: z.string(),
					maxWidth: z.string().optional(),
					minWidth: z.string().optional(),
					tooltip: z.string().optional(),
					width: z.string().optional(),
				})
				.strict(),
		),
		data: z.array(z.any()),
		expandMode: z.enum(['multi', 'single']).nullable().default(null),
		/**
		 * Keep in sync with type expression
		 * @see GetRowBehavior
		 */
		getRowBehavior: z
			.function()
			.args(z.object({ row: z.record(z.unknown()), rowIndex: z.number() }))
			.returns(
				z.object({
					actions: z
						.array(
							z
								.object({
									dataTest: z.string().optional(),
									icon: yocoIconSchema,
									isDisabled: z.boolean().optional(),
									onClick: z
										.function()
										.args()
										.returns(z.union([z.void(), z.promise(z.void())])),
									tooltip: z.string().optional(),
								})
								.strict(),
						)
						.optional(),
					classes: z.array(z.string()).optional(),
					click: z
						.union([
							z.object({
								component: z.null(),
								onClick: z
									.function()
									.args()
									.returns(z.union([z.void(), z.promise(z.void())])),
							}),
							z.object({
								component: z.literal('a'),
								on: z.record(z.unknown()).optional(),
								props: z.object({ href: z.string() }).passthrough(),
							}),
							z.object({
								component: z.string().regex(/^[^a]($|.+)/),
								on: z.record(z.unknown()).optional(),
								props: z.record(z.unknown()).optional(),
							}),
							z.literal('expand'),
						])
						.optional(),
					disable: z
						.object({
							actions: z.boolean(),
							click: z.boolean(),
							expand: z.boolean(),
							select: z.boolean(),
						})
						.optional(),
					id: z.string(),
					isLoading: z.boolean().optional(),
				}),
			),
		hasDragAndDrop: z.boolean().default(false),
		id: z.string(),
		isSelectable: z
			.union([z.boolean(), z.enum(['multi', 'single'])])
			.nullable()
			.default(null)
			.transform((val) => {
				if (typeof val === 'string') return val
				return val ? 'multi' : null
			}),
	})
	.strict()

type InternalKottiTableParameters<
	ROW extends KottiTable.AnyRow,
	COLUMN_ID extends string,
> = {
	columns: KottiTable.Column<ROW, COLUMN_ID>[]
	data: ROW[]
	getRowBehavior: GetRowBehavior<ROW>
} & {
	expandMode: 'multi' | 'single' | null
	hasDragAndDrop: boolean
	id: string
	isSelectable: 'multi' | 'single' | null
}

type KottiTableHook<ROW extends KottiTable.AnyRow, COLUMN_ID extends string> = {
	api: KottiTable.Hook.Returns<COLUMN_ID>
	tableContext: TableContext<ROW, COLUMN_ID>
}

export const useKottiTable = <
	ROW extends KottiTable.AnyRow,
	COLUMN_ID extends string,
>(
	_params: Ref<Readonly<KottiTableParameter<ROW, COLUMN_ID>>>,
): KottiTableHook<ROW, COLUMN_ID> => {
	const params = computed(
		() =>
			paramsSchema.parse(_params.value) as InternalKottiTableParameters<
				ROW,
				COLUMN_ID
			>,
	)
	const columnHelper = createColumnHelper<ROW>()
	const i18nContext = useI18nContext()

	const columnIdSet = computed<Set<COLUMN_ID>>(() => {
		const result = new Set(params.value.columns.map((c) => c.id))

		if (result.size !== params.value.columns.length) {
			throw new Error('Column ids should be unique')
		}

		return result
	})
	const rowIdSet = computed<Set<string>>(() => {
		const { data, getRowBehavior } = params.value
		return new Set(
			data.map((row, rowIndex) => getRowBehavior({ row, rowIndex }).id),
		)
	})

	const columnOrder = useComputedRef<InternalColumnId[], COLUMN_ID[]>({
		get: (value) => value.slice(ARRAY_START) as COLUMN_ID[],
		set: (value) => {
			const newValue: InternalColumnId[] = [
				EXPANSION_COLUMN_ID,
				MULTI_SELECTION_COLUMN_ID,
				SINGLE_SELECTION_COLUMN_ID,
			]
			// Drop unknown columns from outside value
			for (const columnId of value) {
				if (columnIdSet.value.has(columnId) && !newValue.includes(columnId)) {
					newValue.push(columnId)
				} else {
					// eslint-disable-next-line no-console
					console.warn(`useKottiTable: dropped unknown column "${columnId}"`)
				}
			}
			// Append missing column ids
			for (const column of params.value.columns) {
				if (!newValue.includes(column.id)) {
					newValue.push(column.id)
				}
			}
			return newValue
		},
		sync: (oldValue) => [
			EXPANSION_COLUMN_ID,
			MULTI_SELECTION_COLUMN_ID,
			SINGLE_SELECTION_COLUMN_ID,
			...oldValue.filter((columnId) =>
				columnIdSet.value.has(columnId as COLUMN_ID),
			),
		],
		value: ref([
			EXPANSION_COLUMN_ID,
			MULTI_SELECTION_COLUMN_ID,
			SINGLE_SELECTION_COLUMN_ID,
			...params.value.columns.map(({ id }) => id),
		]),
	})

	const selectedRows = useComputedRef<RowSelectionState>({
		get: (value) => value,
		set: (value) => {
			return Object.fromEntries(
				Object.entries(value).filter(([_, isSelected]) => isSelected),
			)
		},
		value: ref({}),
	})

	const expandedRows = useComputedRef<ExpandedState, Record<string, boolean>>({
		get: (value) => {
			if (value === true)
				return Object.fromEntries(
					[...rowIdSet.value].map((rowId) => [rowId, true]),
				)

			return value
		},
		set: (value) => {
			switch (params.value.expandMode) {
				case 'multi':
					return Object.fromEntries(
						Object.entries(value).filter(
							([rowId, isExpanded]) => rowIdSet.value.has(rowId) && isExpanded,
						),
					)
				case null:
					return {}
				case 'single': {
					for (const [rowId, isExpanded] of Object.entries(value)) {
						if (rowIdSet.value.has(rowId) && isExpanded) {
							return { [rowId]: true }
						}
					}
					return {}
				}
			}
		},
		value: ref({}),
	})

	const ordering = useComputedRef<
		SortingState<COLUMN_ID>,
		KottiTable.Ordering<COLUMN_ID>[]
	>({
		get(value) {
			return value.map((sorting) => ({
				id: sorting.id,
				value: sorting.desc ? ('descending' as const) : ('ascending' as const),
			}))
		},
		set(value) {
			return (
				value
					// Remove entries that do not have a valid column
					.filter((sorting) => columnIdSet.value.has(sorting.id))
					.map((sorting) => ({
						desc: sorting.value === 'descending',
						id: sorting.id,
					}))
			)
		},
		sync: (oldValue) => {
			return oldValue.filter(({ id }) => columnIdSet.value.has(id))
		},
		value: ref([]),
	})

	const hiddenColumns = useComputedRef<VisibilityState, Set<COLUMN_ID>>({
		get: (value) => {
			const result = new Set<COLUMN_ID>()

			for (const id of columnIdSet.value) {
				if (
					id === EXPANSION_COLUMN_ID ||
					id === SINGLE_SELECTION_COLUMN_ID ||
					id === MULTI_SELECTION_COLUMN_ID
				)
					continue
				if (value[id] === false) result.add(id)
			}

			return result
		},
		set: (newSet) => {
			const newVisibilityState: VisibilityState = {
				[EXPANSION_COLUMN_ID]: params.value.expandMode !== null,
				[MULTI_SELECTION_COLUMN_ID]: params.value.isSelectable === 'multi',
				[SINGLE_SELECTION_COLUMN_ID]: params.value.isSelectable === 'single',
			}

			for (const id of columnIdSet.value) {
				newVisibilityState[id] = !newSet.has(id)
			}

			return newVisibilityState
		},
		sync: (oldValue) => {
			return {
				...oldValue,
				[EXPANSION_COLUMN_ID]: params.value.expandMode !== null,
				[MULTI_SELECTION_COLUMN_ID]: params.value.isSelectable === 'multi',
				[SINGLE_SELECTION_COLUMN_ID]: params.value.isSelectable === 'single',
			}
		},
		value: ref({
			...Object.fromEntries(
				params.value.columns.map((column) => [column.id, true]),
			),
			[EXPANSION_COLUMN_ID]: params.value.expandMode !== null,
			[MULTI_SELECTION_COLUMN_ID]: params.value.isSelectable === 'multi',
			[SINGLE_SELECTION_COLUMN_ID]: params.value.isSelectable === 'single',
		}),
	})

	const triggerExpand = (rowId: string) => {
		switch (params.value.expandMode) {
			case 'multi': {
				expandedRows.value = {
					...expandedRows.value,
					[rowId]: !(rowId in expandedRows.value),
				}
				break
			}
			case null: {
				expandedRows.value = {}
				break
			}
			case 'single': {
				// eslint-disable-next-line unicorn/prefer-ternary
				if (rowId in expandedRows.value) {
					expandedRows.value = {}
				} else {
					expandedRows.value = { [rowId]: true }
				}
				break
			}
		}
	}

	/**
	 * Used for selecting multiple rows via shift click
	 */
	const rowSelectionAnchorIndex = ref<{
		rowId: string
		rowIndex: number
	} | null>(null)

	const getMovedColumnOrder = (
		fromColumnId: COLUMN_ID,
		toIndex: number,
	): COLUMN_ID[] => {
		const fromIndex = columnOrder.value.indexOf(fromColumnId)
		if (fromIndex === -1)
			throw new Error(`Could not find column id ${fromColumnId}`)

		const newOrder = [...columnOrder.value]
		newOrder.splice(fromIndex, 1)
		newOrder.splice(
			toIndex > fromIndex ? toIndex - 1 : toIndex,
			0,
			fromColumnId,
		)
		return newOrder
	}

	const table = useVueTable<ROW>({
		columns: [
			// Expand Column
			columnHelper.display({
				cell: ({ row }: CellContext<ROW, unknown>) => {
					const rowBehavior = params.value.getRowBehavior({
						row: row.original,
						rowIndex: row.index,
					})
					const isDisabled = rowBehavior.disable?.expand ?? false

					return h(KtButton, {
						ariaExpanded: String(row.getIsExpanded()),
						class: 'kt-table-expand',
						'data-test': `${params.value.id}.column-${EXPANSION_COLUMN_ID}.row-${row.id}.button`,
						disabled: isDisabled,
						icon: row.getIsExpanded()
							? Yoco.Icon.CHEVRON_DOWN
							: Yoco.Icon.CHEVRON_RIGHT,
						onClick: (event: MouseEvent) => {
							event.stopPropagation()
							event.preventDefault()
							if (isDisabled) return
							triggerExpand(row.id)
						},
						type: 'text',
					})
				},
				id: EXPANSION_COLUMN_ID,
				meta: {
					cellClasses: 'kt-table-cell kt-table-cell--is-body',
					disableCellClick: false,
					headerClasses: 'kt-table-cell kt-table-cell--is-header',
					index: 0,
				},
			}),
			// Multi select rows column
			columnHelper.display({
				cell: ({ row }: CellContext<ROW, unknown>) =>
					h(
						'div',
						{
							ariaDisabled: !row.getCanSelect(),
							onClick: (event: MouseEvent) => {
								event.stopPropagation()
								event.preventDefault()

								if (
									event.shiftKey &&
									rowSelectionAnchorIndex.value !== null &&
									rowIdSet.value.has(rowSelectionAnchorIndex.value.rowId)
								) {
									const { rowIndex } = rowSelectionAnchorIndex.value
									const rangeStart = Math.min(rowIndex, row.index)
									const rangeEnd = Math.max(rowIndex, row.index)
									const { rows } = table.getRowModel()
									for (let i = rangeStart; i <= rangeEnd; i++) {
										const rowInRange = rows[i]
										// all rows in the range will get the toggle state that the targetted row gets.
										rowInRange?.toggleSelected(!row.getIsSelected())
									}
								} else {
									row.toggleSelected(!row.getIsSelected())
								}
								rowSelectionAnchorIndex.value = {
									rowId: row.id,
									rowIndex: row.index,
								}
							},
							// this prevents table content selection that would happen with shift clicking
							class: 'kt-table-selection',
							onMousedown: (event: MouseEvent) => {
								event.preventDefault()
							},
						},
						[
							h(ToggleInner, {
								component: 'div',
								inputProps: {
									'data-test': `${params.value.id}.column-${MULTI_SELECTION_COLUMN_ID}.row-${row.id}.checkbox`,
									disabled: !row.getCanSelect(),
									id: `${params.value.id}-${row.id}-select`,
									tabindex: 1,
								},
								isDisabled: !row.getCanSelect(),
								modelValue: row.getIsSelected(),
							}),
						],
					),
				header: ({ table }: HeaderContext<ROW, unknown>) =>
					table.getRowCount() > 0
						? h(
								'div',
								{
									onClick: () => {
										table.toggleAllRowsSelected(!table.getIsAllRowsSelected())
									},
								},
								[
									h(ToggleInner, {
										component: 'div',
										inputProps: {
											'data-test': `${params.value.id}.column-${MULTI_SELECTION_COLUMN_ID}.header.checkbox`,
											disabled: false,
											id: `${params.value.id}-column-select-header`,
											tabindex: 1,
										},
										isDisabled: false,
										modelValue:
											table.getIsAllRowsSelected() ||
											table
												.getRowModel()
												.rows.some((row) => row.getIsSelected())
												? null
												: false,
									}),
								],
							)
						: null,
				id: MULTI_SELECTION_COLUMN_ID,
				meta: {
					cellClasses: 'kt-table-cell kt-table-cell--is-body',
					disableCellClick: false,
					headerClasses: 'kt-table-cell kt-table-cell--is-header',
					index: 1,
				},
			}),
			// Single select rows column
			columnHelper.display({
				cell: ({ row }: CellContext<ROW, unknown>) =>
					h(
						'div',
						{
							ariaDisabled: !row.getCanSelect(),
							class: 'kt-table-selection',
							onClick: (event: MouseEvent) => {
								if (!row.getCanSelect()) return

								event.stopPropagation()
								event.preventDefault()

								const isSelected = !row.getIsSelected()
								table.toggleAllRowsSelected(false)
								row.toggleSelected(isSelected)
							},
						},
						[
							h(ToggleRadio, {
								isChecked: row.getIsSelected(),
								isDisabled: !row.getCanSelect(),
							}),
						],
					),
				header: () => null,
				id: SINGLE_SELECTION_COLUMN_ID,
				meta: {
					cellClasses: 'kt-table-cell kt-table-cell--is-body',
					disableCellClick: false,
					headerClasses: 'kt-table-cell kt-table-cell--is-header',
					index: 2,
				},
			}),
			...params.value.columns.map((column) => {
				const getCellClasses = (cellType: 'body' | 'header') =>
					classNames({
						[`kt-table-cell--is-${cellType}`]: true,
						[`kt-table-cell--is-${column.display.align}-aligned`]: true,
						'kt-table-cell': true,
						'kt-table-cell--displays-number': column.display.isNumeric,
						// 'kt-table-cell--has-drop-indicator':
						// 	index === dropTargetColumnIndex.value,
						// 'kt-table-cell--has-drop-indicator-right':
						// 	index + 1 === dropTargetColumnIndex.value &&
						// 	columnOrder.value.length - 1 === index,
						// 'kt-table-cell--is-dragged': column.id === draggedColumnId.value,
						// 'kt-table-cell--was-successfully-dropped':
						// 	column.id === successfullyDroppedColumnId.value,
					})

				return columnHelper.accessor(column.getData, {
					cell: (info) => {
						const value = info.getValue()

						return (
							column.display.render(value, {
								i18n: i18nContext,
							}) ?? Dashes.EmDash
						)
					},
					enableSorting: column.isSortable,
					header: () =>
						column.tooltip
							? h('div', [
									h('span', column.label),
									h(
										KtPopover,
										{ placement: 'top', trigger: 'hover' },
										{
											content: () => h('span', column.tooltip),
											default: () =>
												h('i', { class: 'yoco' }, Yoco.Icon.CIRCLE_QUESTION),
										},
									),
								])
							: h('div', column.label),
					id: column.id,
					meta: {
						cellClasses: getCellClasses('body'),
						disableCellClick: column.display.disableCellClick,
						headerClasses: getCellClasses('header'),
						index: columnOrder.value.indexOf(column.id),
						sortBehavior: column.display.sortBehavior,
						style: {
							'max-width': column.maxWidth,
							'min-width': column.minWidth,
							width: column.width,
						},
					},
				})
			}),
		],
		get data() {
			return params.value.data
		},
		enableRowSelection: (row) => {
			if (!params.value.isSelectable) return false

			const behavior = params.value.getRowBehavior({
				row: row.original,
				rowIndex: row.index,
			})
			return !behavior.disable?.select
		},
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel:
			params.value.expandMode !== null ? getExpandedRowModel() : undefined,
		getRowId: (row, rowIndex) =>
			params.value.getRowBehavior({ row, rowIndex }).id,
		onColumnVisibilityChange: hiddenColumns.tanstackSetter,
		onRowSelectionChange: selectedRows.tanstackSetter,
		onSortingChange: ordering.tanstackSetter as ReactStyleUpdater<unknown>,
		state: {
			get columnOrder() {
				return columnOrder.tanstackGetter()
			},
			get columnVisibility() {
				return hiddenColumns.tanstackGetter()
			},
			get expanded() {
				return expandedRows.tanstackGetter()
			},
			get rowSelection() {
				return selectedRows.tanstackGetter()
			},
			get sorting() {
				return ordering.tanstackGetter()
			},
		},
	})

	watch(params, (newParams, oldParams) => {
		const oldColumnSet = new Set(oldParams.columns.map((column) => column.id))
		const newColumnSet = new Set(newParams.columns.map((column) => column.id))
		const isDifferent =
			oldColumnSet.size !== newColumnSet.size ||
			[...oldColumnSet.keys()].some((entry) => !newColumnSet.has(entry))
		if (isDifferent) {
			columnOrder.recalculate()
			hiddenColumns.recalculate()
			ordering.recalculate()
		} else {
			hiddenColumns.recalculate()
		}
	})

	const tableContext: TableContext<ROW, COLUMN_ID> = computed(() => ({
		internal: {
			columnOrder: columnOrder.value,
			getColumnIndex: (columnId) => {
				return columnOrder.value.indexOf(columnId)
			},
			getOrdering: () => {
				return ordering.value
			},
			getRowBehavior: params.value.getRowBehavior,
			hasDragAndDrop: params.value.hasDragAndDrop,
			isExpandable: params.value.expandMode !== null,
			selectionCount: Object.values(selectedRows.value).length,
			swapDraggedAndDropTarget: (draggedColumnId, targetIndex) => {
				columnOrder.value = getMovedColumnOrder(draggedColumnId, targetIndex)
			},
			table,
			triggerExpand,
			unselectAllRows: () => {
				selectedRows.value = {}
			},
			visibleColumns: hiddenColumns.tanstackGetter(),
		},
	}))
	useProvideTableContext<ROW, COLUMN_ID>(params.value.id, tableContext)

	return {
		api: {
			columnOrder,
			expandedRows,
			hiddenColumns,
			ordering,
			selectedRows,
		},
		tableContext,
	}
}
